import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, ClipboardList, ChevronRight, Filter, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import SelectLoader from 'shared/components/ui/SelectLoader';
import { fetchCalibrationOptions, CalibrationOptionsParams } from '../../../config/calibrationOptions';
import { SensorTypeService } from '../../calibration/services/sensorType.service';
import { AcceptanceCriteriaService } from '../services/acceptanceCriteria.service';
import { isDisplacementCategory } from '../../calibration/utils/displacementModelFamily';
import { formatMeasurementRangeLabel } from 'features/calibration/utils/rangeUnit';
import { formatSensorType } from 'shared/utils';

interface AcceptanceCriteriaEntry {
  id: string;
  sensorTypeId: string;
  category?: string;
  type?: string;
  model?: string;
  measurement_range?: number;
  acceptance_criteria: Record<string, number>;
}

const ManageAcceptanceCriteria: React.FC = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<AcceptanceCriteriaEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState<Record<string, number>>({});
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 10,
    page: 0,
  });
  const [totalItems, setTotalItems] = useState(0);

  // Applied filters (drive the table query)
  const [selectedType, setSelectedType] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedFamily, setSelectedFamily] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedRange, setSelectedRange] = useState('');

  // Temp filters (edited in the panel before Apply)
  const [tempType, setTempType] = useState('');
  const [tempCategory, setTempCategory] = useState('');
  const [tempFamily, setTempFamily] = useState('');
  const [tempModel, setTempModel] = useState('');
  const [tempRange, setTempRange] = useState('');

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const isApplyingFiltersRef = useRef(false);

  type DropdownOption = { value: string; label: string };
  const [typeOptions, setTypeOptions] = useState<DropdownOption[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<DropdownOption[]>([]);
  const [familyOptions, setFamilyOptions] = useState<DropdownOption[]>([]);
  const [modelOptions, setModelOptions] = useState<DropdownOption[]>([]);
  const [rangeOptions, setRangeOptions] = useState<DropdownOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [loadingFields, setLoadingFields] = useState<Record<string, boolean>>({});
  const trackLoading = useCallback(async (key: string, fn: () => Promise<void>) => {
    setLoadingFields((prev) => ({ ...prev, [key]: true }));
    try {
      await fn();
    } finally {
      setLoadingFields((prev) => ({ ...prev, [key]: false }));
    }
  }, []);

  const hasActiveFilters = useMemo(
    () =>
      !!(
        selectedType ||
        selectedCategory ||
        selectedFamily ||
        selectedModel ||
        selectedRange
      ),
    [selectedType, selectedCategory, selectedFamily, selectedModel, selectedRange],
  );

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (selectedType) count++;
    if (selectedCategory) count++;
    if (selectedFamily) count++;
    if (selectedModel) count++;
    if (selectedRange) count++;
    return count;
  }, [selectedType, selectedCategory, selectedFamily, selectedModel, selectedRange]);

  const loadEntries = useCallback(async () => {
    setIsLoading(true);
    try {
      const rangeNum = selectedRange ? parseFloat(selectedRange) : undefined;
      const result = await SensorTypeService.list({
        skip: paginationModel.page * paginationModel.pageSize,
        limit: paginationModel.pageSize,
        is_active: true,
        ...(selectedType ? { type: selectedType } : {}),
        ...(selectedCategory ? { category: selectedCategory } : {}),
        ...(selectedFamily ? { family: selectedFamily } : {}),
        ...(selectedModel ? { model: selectedModel } : {}),
        ...(rangeNum !== undefined && !Number.isNaN(rangeNum)
          ? { measurement_range: rangeNum }
          : {}),
      });

      const mapped: AcceptanceCriteriaEntry[] = result.items.map((sensorType) => ({
        id: sensorType.id,
        sensorTypeId: sensorType.id,
        category: sensorType.category,
        type: sensorType.type,
        model: sensorType.model,
        measurement_range: sensorType.measurement_range,
        acceptance_criteria: sensorType.acceptance_criteria || {},
      }));

      setEntries(mapped);
      setTotalItems(result.total);
    } catch (error: any) {
      console.error('Error loading acceptance criteria:', error);
      toast.error(error.message || 'Failed to load acceptance criteria');
      setEntries([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    selectedType,
    selectedCategory,
    selectedFamily,
    selectedModel,
    selectedRange,
  ]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  // Reset to first page when applied filters change
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [
    selectedType,
    selectedCategory,
    selectedFamily,
    selectedModel,
    selectedRange,
    paginationModel.pageSize,
  ]);

  // Dropdowns are loaded directly from their dependent select changes.
  const loadCategoryOptions = useCallback(async (type: string) => {
    if (!type) {
      setCategoryOptions([]);
      return;
    }
    try {
      const response = await fetchCalibrationOptions({ sensor_type: type });
      setCategoryOptions(response.category);
    } catch {
      setCategoryOptions([]);
    }
  }, []);

  const loadFamilyOptions = useCallback(async (type: string, category: string) => {
    if (!type || !category || !isDisplacementCategory(category)) {
      setFamilyOptions([]);
      return;
    }
    try {
      const response = await fetchCalibrationOptions({ sensor_type: type, category });
      setFamilyOptions(response.displacementSensorType);
    } catch {
      setFamilyOptions([]);
    }
  }, []);

  const loadModelOptions = useCallback(
    async (type: string, category: string, family = '') => {
      if (!type || !category || (isDisplacementCategory(category) && !family)) {
        setModelOptions([]);
        return;
      }
      try {
        const params: CalibrationOptionsParams = {
          sensor_type: type,
          category,
        };
        if (isDisplacementCategory(category)) {
          params.model_family = family;
        }
        const response = await fetchCalibrationOptions(params);
        setModelOptions(response.model);
      } catch {
        setModelOptions([]);
      }
    },
    [],
  );

  const loadRangeOptions = useCallback(async (type: string, category: string, model: string) => {
    if (!type || !category || !model) {
      setRangeOptions([]);
      return;
    }
    try {
      const response = await fetchCalibrationOptions({ sensor_type: type, category, model });
      setRangeOptions(response.range);
    } catch {
      setRangeOptions([]);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadTypeOptions = async () => {
      setOptionsLoading(true);
      try {
        const response = await fetchCalibrationOptions();
        if (!cancelled) setTypeOptions(response.sensorType);
      } catch {
        if (!cancelled) setTypeOptions([]);
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    };

    void loadTypeOptions();
    return () => {
      cancelled = true;
    };
  }, []);

  // Sync temp from applied when panel opens
  useEffect(() => {
    if (isFilterPanelOpen) {
      setTempType(selectedType);
      setTempCategory(selectedCategory);
      setTempFamily(selectedFamily);
      setTempModel(selectedModel);
      setTempRange(selectedRange);
    }
  }, [
    isFilterPanelOpen,
    selectedType,
    selectedCategory,
    selectedFamily,
    selectedModel,
    selectedRange,
  ]);

  // Reset dependent applied filters when parent changes (skip while applying)
  useEffect(() => {
    if (isApplyingFiltersRef.current) return;
    setSelectedCategory('');
    setSelectedFamily('');
    setSelectedModel('');
    setSelectedRange('');
  }, [selectedType]);

  useEffect(() => {
    if (isApplyingFiltersRef.current) return;
    setSelectedFamily('');
    setSelectedModel('');
    setSelectedRange('');
  }, [selectedCategory]);

  useEffect(() => {
    if (isApplyingFiltersRef.current) return;
    setSelectedModel('');
    setSelectedRange('');
  }, [selectedFamily]);

  useEffect(() => {
    if (isApplyingFiltersRef.current) return;
    setSelectedRange('');
  }, [selectedModel]);

  // Reset editing state when page changes
  useEffect(() => {
    if (editingId) {
      setEditingId(null);
      setEditFormData({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page]);

  const handleApplyFilters = () => {
    isApplyingFiltersRef.current = true;
    setSelectedType(tempType);
    setSelectedCategory(tempCategory);
    setSelectedFamily(
      isDisplacementCategory(tempCategory) ? tempFamily : '',
    );
    setSelectedModel(tempModel);
    setSelectedRange(tempRange);
    setTimeout(() => {
      isApplyingFiltersRef.current = false;
    }, 100);
    setIsFilterPanelOpen(false);
  };

  const handleClearFilters = () => {
    setTempType('');
    setTempCategory('');
    setTempFamily('');
    setTempModel('');
    setTempRange('');
    setSelectedType('');
    setSelectedCategory('');
    setSelectedFamily('');
    setSelectedModel('');
    setSelectedRange('');
    setIsFilterPanelOpen(false);
  };

  const handleEdit = (entry: AcceptanceCriteriaEntry) => {
    setEditingId(entry.id);
    setEditFormData({ ...entry.acceptance_criteria });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditFormData({});
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;

    const invalidEntries = Object.entries(editFormData).filter(([_, value]) => value < 0);
    if (invalidEntries.length > 0) {
      toast.error('All criteria values must be non-negative (>= 0)');
      return;
    }

    try {
      await AcceptanceCriteriaService.updateAcceptanceCriteria(editingId, editFormData);
      toast.success('Acceptance criteria updated successfully');
      setEditingId(null);
      setEditFormData({});
      await loadEntries();
    } catch (error: any) {
      let errorMessage = error.response?.data?.detail || error.message || 'Failed to update acceptance criteria';
      if(Array.isArray(errorMessage)) {
        errorMessage = errorMessage.map((err: any) => err.loc?.[1] + ': ' + err.msg).join(', ');
      } else if(typeof errorMessage !== 'string') {
        errorMessage = 'Failed to update acceptance criteria';
      }
      toast.error(errorMessage);
    }
  };

  const handleCriteriaChange = (key: string, value: string) => {
    const numValue = parseFloat(value);
    setEditFormData((prev) => ({
      ...prev,
      [key]: numValue,
    }));
  };

  const columns: GridColDef[] = [
    {
      field: 'row_number',
      headerName: '#',
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        // Prefer local page index — getRowIndexRelativeToVisibleRows can be
        // undefined/null during server-pagination page transitions (esp. going back).
        const apiIndex = params.api.getRowIndexRelativeToVisibleRows(params.id);
        const indexOnPage =
          typeof apiIndex === 'number' && Number.isFinite(apiIndex) && apiIndex >= 0
            ? apiIndex
            : entries.findIndex((e) => e.id === String(params.id));
        const rowNumber =
          paginationModel.page * paginationModel.pageSize +
          (indexOnPage >= 0 ? indexOnPage : 0) +
          1;
        return (
          <div className="text-sm text-gray-500 font-medium">{rowNumber}</div>
        );
      },
    },
    {
      field: 'category',
      headerName: 'Category',
      flex: 1,
      minWidth: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <span className="text-sm font-medium text-gray-900">{params.value || 'N/A'}</span>
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 1,
      minWidth: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <span className="text-sm text-gray-900">{formatSensorType(params.value)}</span>
      ),
    },
    {
      field: 'model',
      headerName: 'Model',
      flex: 0.9,
      minWidth: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <span className="text-sm text-gray-900">{params.value || 'N/A'}</span>
      ),
    },
    {
      field: 'measurement_range',
      headerName: 'Range',
      flex: 0.9,
      minWidth: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row as AcceptanceCriteriaEntry;
        return (
          <span className="text-sm text-gray-900">
            {row.measurement_range != null
              ? formatMeasurementRangeLabel(row.measurement_range, undefined, row.category)
              : 'N/A'}
          </span>
        );
      },
    },
    {
      field: 'acceptance_criteria',
      headerName: 'Acceptance Criteria',
      flex: 2,
      minWidth: 300,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row as AcceptanceCriteriaEntry;
        if (editingId === row.id) {
          return (
            <div className="space-y-2 py-2">
              {Object.entries(editFormData).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-gray-700">{key}:</span>
                  <input
                    type="number"
                    value={value}
                    onChange={(e) => handleCriteriaChange(key, e.target.value)}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                    }}
                    min="0"
                    step="0.01"
                    className="px-2 py-1 text-xs border border-gray-300 rounded w-20"
                  />
                </div>
              ))}
            </div>
          );
        }

        if (Object.keys(row.acceptance_criteria).length === 0) {
          return <span className="text-sm text-gray-400">No criteria defined</span>;
        }

        return (
          <div className="space-y-1">
            {Object.entries(row.acceptance_criteria).map(([key, value]) => (
              <div key={key} className="text-xs">
                <span className="font-medium text-gray-700">{key}:</span>{' '}
                <span className="text-gray-600">±{value}</span>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Action',
      flex: 0.8,
      minWidth: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const row = params.row as AcceptanceCriteriaEntry;

        if (editingId === row.id) {
          return (
            <div className="flex items-center justify-center gap-2 h-full">
              <button
                onClick={handleSaveEdit}
                className="px-3 py-1 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
              >
                Save
              </button>
              <button
                onClick={handleCancelEdit}
                className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          );
        }

        return (
          <button
            onClick={() => handleEdit(row)}
            title="Update"
            aria-label="Update"
            className="flex items-center justify-center p-1.5 text-primary-600 bg-primary-50 rounded-md hover:bg-primary-100"
          >
            <Pencil className="h-4 w-4" />
          </button>
        );
      },
    },
  ];

  return (
    <div>
      <div className="flex items-center space-x-2 text-sm text-gray-500 border-b border-gray-200 pb-4 mb-6">
        <button
          onClick={() => navigate('/')}
          className="hover:text-gray-700 transition-colors cursor-pointer"
          title="Back to Dashboard"
        >
          Dashboard
        </button>
        <ChevronRight className="h-4 w-4" />
        <span className="text-primary-600 font-medium">Manage Acceptance Criteria</span>
      </div>

      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <ClipboardList className="h-8 w-8 flex-shrink-0 text-primary-600 mt-0.5" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Manage Acceptance Criteria</h1>
            <p className="mt-1 text-sm text-gray-600">
              View and update acceptance criteria for sensor types
            </p>
          </div>
        </div>

        <button
          onClick={() => setIsFilterPanelOpen((prev) => !prev)}
          className="flex items-center justify-center gap-2 whitespace-nowrap btn-primary"
        >
          <Filter className="h-4 w-4" />
          {isFilterPanelOpen ? 'Hide Filter' : 'Apply Filter'}
          {hasActiveFilters && (
            <span className="ml-1 px-2 py-0.5 text-xs font-semibold bg-white bg-opacity-20 rounded-full">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {isFilterPanelOpen && (
        <div className="card mb-4 p-4 sm:p-5 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Filter className="h-5 w-5 text-primary-600" />
              Filters
            </h2>
            <button
              onClick={() => setIsFilterPanelOpen(false)}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              aria-label="Close filters"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
              <SelectLoader loading={optionsLoading}>
              <select
                className="input text-sm w-full"
                value={tempType}
                disabled={optionsLoading}
                onChange={(e) => {
                  const type = e.target.value;
                  setTempType(type);
                  setTempCategory('');
                  setTempFamily('');
                  setTempModel('');
                  setTempRange('');
                  setCategoryOptions([]);
                  setFamilyOptions([]);
                  setModelOptions([]);
                  setRangeOptions([]);
                  void trackLoading('category', () => loadCategoryOptions(type));
                }}
              >
                <option value="">All Types</option>
                {optionsLoading ? (
                  <option value="" disabled>
                    Loading...
                  </option>
                ) : (
                  typeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))
                )}
              </select>
              </SelectLoader>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <SelectLoader loading={!!loadingFields.category}>
              <select
                className="input text-sm w-full"
                value={tempCategory}
                disabled={optionsLoading || !tempType}
                onChange={(e) => {
                  const category = e.target.value;
                  setTempCategory(category);
                  setTempFamily('');
                  setTempModel('');
                  setTempRange('');
                  setFamilyOptions([]);
                  setModelOptions([]);
                  setRangeOptions([]);
                  if (isDisplacementCategory(category)) {
                    void trackLoading('family', () => loadFamilyOptions(tempType, category));
                  } else {
                    void trackLoading('model', () => loadModelOptions(tempType, category));
                  }
                }}
              >
                <option value="">All Categories</option>
                {!tempType ? (
                  <option value="" disabled>
                    Select Type first
                  </option>
                ) : (
                  categoryOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))
                )}
              </select>
              </SelectLoader>
            </div>

            {isDisplacementCategory(tempCategory) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Displacement Family
                </label>
                <SelectLoader loading={!!loadingFields.family}>
                <select
                  className="input text-sm w-full"
                  value={tempFamily}
                  disabled={
                    optionsLoading ||
                    !tempType ||
                    !tempCategory ||
                    familyOptions.length === 0
                  }
                  onChange={(e) => {
                    const family = e.target.value;
                    setTempFamily(family);
                    setTempModel('');
                    setTempRange('');
                    setModelOptions([]);
                    setRangeOptions([]);
                    void trackLoading('model', () => loadModelOptions(tempType, tempCategory, family));
                  }}
                >
                  <option value="">All Families</option>
                  {familyOptions.length === 0 ? (
                    <option value="" disabled>
                      No families available
                    </option>
                  ) : (
                    familyOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))
                  )}
                </select>
                </SelectLoader>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
              <SelectLoader loading={!!loadingFields.model}>
              <select
                className="input text-sm w-full"
                value={tempModel}
                disabled={
                  optionsLoading ||
                  !tempType ||
                  !tempCategory ||
                  (isDisplacementCategory(tempCategory) && !tempFamily) ||
                  modelOptions.length === 0
                }
                onChange={(e) => {
                  const model = e.target.value;
                  setTempModel(model);
                  setTempRange('');
                  setRangeOptions([]);
                  void trackLoading('range', () => loadRangeOptions(tempType, tempCategory, model));
                }}
              >
                <option value="">All Models</option>
                {!tempType ? (
                  <option value="" disabled>
                    Select Type first
                  </option>
                ) : !tempCategory ? (
                  <option value="" disabled>
                    Select Category first
                  </option>
                ) : isDisplacementCategory(tempCategory) && !tempFamily ? (
                  <option value="" disabled>
                    Select Family first
                  </option>
                ) : modelOptions.length === 0 ? (
                  <option value="" disabled>
                    No models available
                  </option>
                ) : (
                  modelOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))
                )}
              </select>
              </SelectLoader>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Measurement Range
              </label>
              <SelectLoader loading={!!loadingFields.range}>
              <select
                className="input text-sm w-full"
                value={tempRange}
                disabled={
                  optionsLoading ||
                  !tempType ||
                  !tempCategory ||
                  !tempModel ||
                  rangeOptions.length === 0
                }
                onChange={(e) => setTempRange(e.target.value)}
              >
                <option value="">All Ranges</option>
                {!tempType ? (
                  <option value="" disabled>
                    Select Type first
                  </option>
                ) : !tempCategory ? (
                  <option value="" disabled>
                    Select Category first
                  </option>
                ) : !tempModel ? (
                  <option value="" disabled>
                    Select Model first
                  </option>
                ) : rangeOptions.length === 0 ? (
                  <option value="" disabled>
                    No ranges available
                  </option>
                ) : (
                  rangeOptions.map((opt) => {
                    return (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    );
                  })
                )}
              </select>
              </SelectLoader>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-200">
            <button onClick={handleApplyFilters} className="btn-primary">
              Apply
            </button>
            <button onClick={handleClearFilters} className="btn-secondary">
              Clear
            </button>
          </div>
        </div>
      )}

      {!isLoading && entries.length === 0 ? (
        <div className="card p-8 text-center">
          <p className="text-gray-600">No acceptance criteria found.</p>
        </div>
      ) : (
        <DataGrid
          columns={columns}
          rows={entries}
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={totalItems}
          loading={isLoading}
          paginationMode="server"
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          getRowHeight={() => {
            return 'auto';
          }}
          sx={{
            backgroundColor: 'white',
            borderRadius: '0.5rem',
            border: '1px solid #e5e7eb',

            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f3f4f6',
              borderBottom: '2px solid #d1d5db',
            },

            '& .MuiDataGrid-columnHeader': {
              fontSize: '0.875rem',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: '#374151',
              padding: '12px 16px',
            },

            '& .MuiDataGrid-row': {
              borderBottom: '1px solid #e5e7eb',

              '&:hover': {
                backgroundColor: '#f9fafb',
              },
            },

            '& .MuiDataGrid-cell': {
              padding: '16px',
              fontSize: '0.875rem',
              color: '#111827',
            },

            // Remove cell focus outline
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },

            '& .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },

            '& .MuiDataGrid-footerContainer': {
              backgroundColor: '#f9fafb',
              borderTop: '1px solid #e5e7eb',
            },

            '& .MuiTablePagination-root': {
              fontSize: '0.875rem',
            },
          }}
        />
      )}
    </div>
  );
};

export default ManageAcceptanceCriteria;